import { type ReactElement, useEffect, useState } from 'react';
import { IconButton } from './primitives';

export interface CodeRefProps {
  /** The whole identifier (a UUID, a long code). */
  readonly value: string;
  /** How many trailing characters the short form keeps (default 8). The whole value is always on hover. */
  readonly tail?: number;
  /**
   * The copy action's label (translated by the app). Omit it to draw no copy button — inside a clickable row (the row is
   * itself a button and a button cannot hold another); the whole value is then on hover and the record's drawer copies it.
   */
  readonly copyLabel?: string;
  /** Said for a moment after a successful copy (translated by the app). */
  readonly copiedLabel?: string;
  /** Show the whole value instead of the tail (a drawer or a record has the room). */
  readonly full?: boolean;
}

/**
 * D619 (owner 2026-10-18: "os IDs são tão grandes que fica difícil analisar; passar o mouse, ver o código todo e copiar"): the one way
 * a console writes a long identifier. A list shows its short tail in tabular mono with the whole value on hover; where there is
 * room for a control the copy button puts the whole value on the clipboard.
 */
export function CodeRef({ value, tail = 8, copyLabel, copiedLabel, full = false }: CodeRefProps): ReactElement {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);
  const shown = full || value.length <= tail + 1 ? value : `…${value.slice(-tail)}`;
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <span className="vello-coderef">
      <span className="vello-coderef__value tnum" title={value}>{shown}</span>
      {copyLabel !== undefined && (
        <IconButton icon={copied ? 'check' : 'copy'} label={copied && copiedLabel !== undefined ? copiedLabel : copyLabel} size={14} onClick={(e) => { e.stopPropagation(); void copy(); }} />
      )}
    </span>
  );
}
